import { Button, Descriptions, Modal, Space, Spin, Tag, Typography } from "antd";
import { LinkOutlined, SelectOutlined } from "@ant-design/icons";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import SectionWrapper from "../../../components/SectionWrapper";
import { AppStateContext } from "../../../context/AppContext";
import apiPath from "../../../constants/apiPath";
import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useRequest from "../../../hooks/useRequest";

const { Paragraph } = Typography;

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleString();
};

const names = (items = []) => {
  if (!Array.isArray(items) || !items.length) {
    return "-";
  }
  return items.map((item) => item?.name || item?.title || item?.slug || item).filter(Boolean).join(", ") || "-";
};

const affiliateStatus = (product) => {
  if (product?.is_affiliated || product?.affiliateLink) {
    return { label: "Completed", color: "green" };
  }
  if (product?.is_affiliate_check) {
    return { label: "Failed", color: "red" };
  }
  return { label: "Pending", color: "gold" };
};

const LinkButton = ({ href, children }) => {
  if (!href) {
    return <span>-</span>;
  }

  return (
    <Button type="primary" icon={<SelectOutlined />} onClick={() => window.open(href, "_blank", "noopener,noreferrer")}>
      {children}
    </Button>
  );
};

function ProductDetails() {
  const heading = "Product Details";
  const { id } = useParams();
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const requestRef = useRef(request);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [updatingAffiliate, setUpdatingAffiliate] = useState(false);
  const [affiliatePreview, setAffiliatePreview] = useState(null);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const fetchProduct = useCallback(() => {
    if (!id) {
      return;
    }

    setLoading(true);
    requestRef.current({
      url: `${apiPath.product}/${id}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setProduct(data);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load product", Severty.ERROR);
      },
    });
  }, [id]);

  useEffect(() => {
    setPageHeading(heading);
    fetchProduct();
  }, [fetchProduct, setPageHeading]);

  const handleGenerate = () => {
    setGenerating(true);
    requestRef.current({
      url: `${apiPath.product}/${id}/affiliate?preview=true`,
      method: "POST",
      onSuccess: (response) => {
        setAffiliatePreview(response?.data || null);
        setGenerating(false);
      },
      onError: (error) => {
        setGenerating(false);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to generate affiliate link", Severty.ERROR);
        fetchProduct();
      },
    });
  };

  const handleUpdateAffiliate = () => {
    if (!affiliatePreview?.newAffiliateLink) {
      return;
    }

    setUpdatingAffiliate(true);
    requestRef.current({
      url: `${apiPath.product}/${id}/affiliate`,
      method: "POST",
      data: {
        affiliateLink: affiliatePreview.newAffiliateLink,
        affiliate_source: affiliatePreview.affiliate_source,
      },
      onSuccess: (response) => {
        ShowToast(response?.message || "Affiliate link updated successfully", Severty.SUCCESS);
        setUpdatingAffiliate(false);
        setAffiliatePreview(null);
        fetchProduct();
      },
      onError: (error) => {
        setUpdatingAffiliate(false);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to update affiliate link", Severty.ERROR);
      },
    });
  };

  const status = affiliateStatus(product);

  return (
    <SectionWrapper
      cardHeading={heading}
      showBack
      extra={
        <Button className="primary_btn btnStyle" loading={generating} disabled={generating || !product?._id} onClick={handleGenerate}>
          <LinkOutlined />
          {lang("Generate Affiliate Link")}
        </Button>
      }
    >
      <Spin spinning={loading}>
        {product ? (
          <Descriptions bordered column={1} size="middle">
            <Descriptions.Item label={lang("Title")}>{formatValue(product.title)}</Descriptions.Item>
            <Descriptions.Item label={lang("Product Type")}>{formatValue(product.productType)}</Descriptions.Item>
            <Descriptions.Item label={lang("Description")}>
              <Paragraph ellipsis={{ rows: 4, expandable: true, symbol: "more" }}>{formatValue(product.description)}</Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label={lang("Categories")}>{names(product.category_ids)}</Descriptions.Item>
            <Descriptions.Item label={lang("Sub Categories")}>{names(product.sub_category_ids)}</Descriptions.Item>
            <Descriptions.Item label={lang("Occasions")}>{names(product.occasion_ids)}</Descriptions.Item>
            <Descriptions.Item label={lang("Keywords")}>{names(product.keywords_ids)}</Descriptions.Item>
            <Descriptions.Item label={lang("Price")}>{formatValue(product.price)}</Descriptions.Item>
            <Descriptions.Item label={lang("Stock")}>{formatValue(product.stock)}</Descriptions.Item>
            <Descriptions.Item label={lang("Source URL")}>
              <LinkButton href={product.sourceUrl}>{lang("Open Source URL")}</LinkButton>
            </Descriptions.Item>
            <Descriptions.Item label={lang("Affiliate Status")}>
              <Tag color={status.color}>{lang(status.label)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={lang("Affiliate Provider")}>{formatValue(product.affiliate_source)}</Descriptions.Item>
            <Descriptions.Item label={lang("Affiliate Link")}>
              <LinkButton href={product.affiliateLink}>{lang("Open Affiliate Link")}</LinkButton>
            </Descriptions.Item>
            <Descriptions.Item label={lang("Affiliate Error")}>{formatValue(product.affiliate_error)}</Descriptions.Item>
            <Descriptions.Item label={lang("Affiliate Sync Time")}>{formatDate(product.affiliate_last_sync)}</Descriptions.Item>
            <Descriptions.Item label={lang("Created At")}>{formatDate(product.created_at)}</Descriptions.Item>
            <Descriptions.Item label={lang("Updated At")}>{formatDate(product.updated_at)}</Descriptions.Item>
          </Descriptions>
        ) : (
          <div className="text-center p-4">{lang("Product not found")}</div>
        )}
      </Spin>

      <Modal
        title={lang("Confirm Affiliate Link Update")}
        visible={Boolean(affiliatePreview)}
        onCancel={() => setAffiliatePreview(null)}
        footer={[
          <Button key="cancel" onClick={() => setAffiliatePreview(null)} disabled={updatingAffiliate}>
            {lang("Cancel")}
          </Button>,
          <Button key="update" type="primary" loading={updatingAffiliate} disabled={!affiliatePreview?.newAffiliateLink} onClick={handleUpdateAffiliate}>
            {lang("Update")}
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <strong>{lang("Old Link")}</strong>
            <div className="mt-2">
              <LinkButton href={affiliatePreview?.oldAffiliateLink}>{lang("Open Old Link")}</LinkButton>
            </div>
          </div>
          <div>
            <strong>{lang("New Affiliate Link")}</strong>
            <div className="mt-2">
              <LinkButton href={affiliatePreview?.newAffiliateLink}>{lang("Open New Affiliate Link")}</LinkButton>
            </div>
          </div>
        </Space>
      </Modal>
    </SectionWrapper>
  );
}

export default ProductDetails;
